<script setup lang="ts">

import AboutUsHero from "@/pages/AboutUs/AboutUsHero.vue";
import AboutSection from "@/components/AboutSection/AboutSection.vue";
import AboutProfile from "@/components/AboutProfile/AboutProfile.vue";
import {onMounted, ref, watch} from "vue";
import BioModal from "@/pages/AboutUs/components/BioModal.vue";
import {useAboutStore} from "@/stores/aboutStore";

const aboutStore = useAboutStore();

const aboutImage1 = new URL('@/assets/about/about_us1.jpg', import.meta.url).href;
const aboutImage2 = new URL('@/assets/about/about_us2.jpg', import.meta.url).href;
const aboutImage3 = new URL('@/assets/about/about_us3.jpg', import.meta.url).href;


const selectedMember = ref(null);
const showBio = ref(false);

function openBio(member: any) {
  selectedMember.value = member;
  showBio.value = true;
}

function closeBio() {
  showBio.value = false;
  selectedMember.value = null;
}

onMounted(() => {
  aboutStore.getTeam();
});

// Body scroll lock if popup displayed
watch(() => showBio.value, (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.documentElement.style.overflow = 'auto';
  }
});

</script>

<template>
    <AboutUsHero/>
    <div class="container mx-auto pt-24 px-4 ">
      <div class="w-full flex justify-center">
        <div class="flex flex-col items-center text-center gap-4 mb-32 max-w-3xl">
          <img src="@/assets/icons/quotation.png" alt="Quotation mark">
          <span class="text-primary-blueGrey responsive-title-1">At Cosmos Luxury Yachts, we are passionate about creating exceptional yacht experiences for our clients.</span>
          <span class="text-bw-midGrey responsive-body-xlarge font-bold max-w-2xl">Our mission is to provide the ultimate luxury yacht charter service, tailored to your specific needs and desires.</span>
        </div>
      </div>

      <div class="flex flex-col gap-25">
        <AboutSection :image="aboutImage1" >
          <template #title>
            <p class="text-primary-darkBlue">
              We understand that every client is unique, and we strive to deliver a personalized and unforgettable experience from start to finish.
            </p>
          </template>
          <template #content>
            <p class="text-bw-midGrey">
              Our fleet of yachts is meticulously maintained and equipped with the latest technology, ensuring that you have everything you need for a comfortable and enjoyable voyage.
            </p>
          </template>
        </AboutSection>

        <AboutSection :image="aboutImage2" :reverse="true">
          <template #title>
            <p class="text-primary-darkBlue">
              Our experienced crew members are dedicated to providing exceptional service and hospitality, ensuring that your every need is met during your time on board.
            </p>
          </template>
          <template #content>
            <p class="text-bw-midGrey">
              Whether you’re looking for a peaceful retreat to escape the stresses of everyday life, or an adventurous journey to explore new horizons, we have the expertise to make it happen.
            </p>
          </template>
        </AboutSection>

        <AboutSection :image="aboutImage3" >
          <template #title>
            <p class="text-primary-darkBlue">
              At Cosmos Luxury Yachts, we believe that luxury is more than just material possessions. It’s about creating unforgettable memories and experiences that will last a lifetime.
            </p>
          </template>
          <template #content>
            <p class="text-bw-midGrey">
              Let us take you on a journey of discovery and luxury that will leave you with memories to cherish forever.
            </p>
          </template>
        </AboutSection>
      </div>

    </div>
    <div class="mt-24 p-16 flex flex-col bg-background-beige w-full place-content-center">

      <div class="container">
        <div class="responsive-title-1 text-center mb-15 text-nowrap text-primary-darkBlue font-thin">Our Team</div>


        <!-- First row: First 4 profiles -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-16">
          <AboutProfile
              v-for="(member, index) in aboutStore.team"
              :key="index"
              :title="member.title"
              :name="member.name"
              :image="member.image"
              @view-bio="openBio(member)"
          />
        </div>
      </div>
    </div>

    <!-- Bio Modal -->
    <BioModal v-if="showBio" :member="selectedMember" :show-bio-modal="showBio" @close="closeBio" />

</template>

<style scoped>

</style>
